import React, { useCallback, useEffect, useState } from 'react';
import { fetchAdminFinanceReport } from '../../api/adminFinanceApi';
import { adminDashboardErrorMessage } from '../../utils/adminDashboardAuth';
import { adminDiag } from '../../utils/adminDiagnostics';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import Card from '../ui/Card';
import { typography } from '../../design-system/tokens';

function money(val) {
  if (val == null || Number.isNaN(Number(val))) return '—';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MetricRow({ label, value, tone = 'text-gray-200' }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-800/80 last:border-0">
      <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</span>
      <span className={`text-sm font-black tabular-nums ${tone}`}>{value}</span>
    </div>
  );
}

function ExceptionTable({ title, rows, columns }) {
  if (!rows?.length) return null;
  return (
    <Card variant="admin" className="p-4">
      <p className={`${typography.microWide} text-violet-300 mb-3`}>{title} ({rows.length})</p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="text-gray-500 uppercase tracking-widest">
              {columns.map((col) => (
                <th key={col.key} className="pb-2 pr-3 font-black">{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.id || row.challengeId || idx} className="border-t border-gray-800/60 text-gray-300">
                {columns.map((col) => (
                  <td key={col.key} className="py-2 pr-3 font-mono text-[11px]">
                    {col.format ? col.format(row[col.key], row) : String(row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function AdminFinanceDashboard({ userEmail }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lite, setLite] = useState(false);

  useEffect(() => {
    adminDiag('admin_dashboard_rendered');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminFinanceReport({ lite });
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(adminDashboardErrorMessage(err, 'Could not load finance report'));
    } finally {
      setLoading(false);
    }
  }, [lite]);

  useEffect(() => {
    load();
  }, [load]);

  const healthy = report?.healthy;
  const warnings = report?.warnings || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-8 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`${typography.microWide} text-violet-400 mb-1`}>LockedIn Finance</p>
            <h1 className="text-2xl font-black tracking-tight">Admin dashboard</h1>
            <p className="text-xs text-gray-500 mt-1">
              Read-only · {userEmail || 'signed in'}
              {report?.generatedAt ? ` · ${new Date(report.generatedAt).toLocaleString()}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/admin/groups" className="text-xs font-bold text-violet-400 hover:text-violet-300">
              Groups dashboard
            </a>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={lite}
                onChange={(e) => setLite(e.target.checked)}
                className="rounded border-gray-600"
              />
              Lite mode
            </label>
            <Button type="button" variant="admin" size="sm" disabled={loading} onClick={load}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
        </header>

        {error && (
          <Alert tone="error" className="text-sm font-bold">{error}</Alert>
        )}

        {report && (
          <>
            <Card
              variant="admin"
              className={`p-5 border-2 ${healthy ? 'border-emerald-500/40' : 'border-red-500/50'}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`inline-flex h-3 w-3 rounded-full ${healthy ? 'bg-emerald-400' : 'bg-red-400 animate-pulse'}`}
                />
                <p className="text-lg font-black uppercase tracking-widest">
                  {healthy ? 'Solvent' : 'Solvency risk'}
                </p>
                {report.lite && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                    Lite
                  </span>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Total cash</p>
                  <p className="text-3xl font-black text-emerald-300 tabular-nums">{money(report.total_cash)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Total obligations</p>
                  <p className="text-3xl font-black text-amber-300 tabular-nums">{money(report.total_obligations)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Solvency margin</p>
                  <p className={`text-2xl font-black tabular-nums ${(report.solvency_margin ?? 0) < 0 ? 'text-red-400' : 'text-emerald-300'}`}>
                    {money(report.solvency_margin)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Stripe available / pending</p>
                  <p className="text-lg font-black tabular-nums text-gray-200">
                    {money(report.stripe_available)} / {money(report.stripe_pending)}
                  </p>
                </div>
              </div>
            </Card>

            <Card
              variant="admin"
              className={`p-5 border-2 ${
                (report.safe_to_withdraw ?? 0) > 0
                  ? 'border-emerald-500/40'
                  : 'border-gray-700'
              }`}
            >
              <p className={`${typography.microWide} text-violet-300 mb-1`}>Owner distribution</p>
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-3">
                Safe to withdraw
              </p>
              <p
                className={`text-3xl font-black tabular-nums mb-3 ${
                  (report.safe_to_withdraw ?? 0) > 0 ? 'text-emerald-300' : 'text-gray-400'
                }`}
              >
                {money(report.safe_to_withdraw)}
              </p>
              <p className="text-sm font-bold text-gray-300 leading-relaxed mb-4">
                {report.owner_withdrawal_message || '—'}
              </p>
              <div className="space-y-0 border-t border-gray-800/80 pt-3">
                <MetricRow label="Stripe available" value={money(report.stripe_available)} />
                <MetricRow label="Stripe pending" value={money(report.stripe_pending)} />
                <MetricRow label="Wallet liabilities" value={money(report.wallet_liabilities)} />
                <MetricRow label="Pending withdrawals" value={money(report.pending_withdrawals)} />
                <MetricRow label="Active escrow" value={money(report.active_escrow)} />
                <MetricRow label="Safety reserve" value={money(report.safety_reserve)} />
                <MetricRow
                  label="Safe to withdraw"
                  value={money(report.safe_to_withdraw)}
                  tone={(report.safe_to_withdraw ?? 0) > 0 ? 'text-emerald-300' : 'text-gray-400'}
                />
              </div>
              <p className="text-[10px] text-gray-600 mt-4 leading-relaxed">
                Read-only guidance · uses Stripe available only · does not initiate transfers
              </p>
            </Card>

            <Card variant="admin" className="p-4">
              <p className={`${typography.microWide} text-gray-500 mb-2`}>Obligations</p>
              <MetricRow label="Wallet liabilities" value={money(report.wallet_liabilities)} />
              <MetricRow label="Pending withdrawals" value={money(report.pending_withdrawals)} />
              <MetricRow label="Active escrow" value={money(report.active_escrow)} />
              <MetricRow label="Operating balance (avail − obligations)" value={money(report.available_operating_balance)} />
            </Card>

            <Card variant="admin" className="p-4">
              <p className={`${typography.microWide} text-gray-500 mb-2`}>Revenue & fees</p>
              <MetricRow label="Platform fees (realized)" value={money(report.platform_fees_realized)} />
              <MetricRow label="Platform fees (settlement)" value={money(report.platform_fees_settlement)} />
              <MetricRow label="Platform fees (unclaimed)" value={money(report.platform_fees_unclaimed)} />
              <MetricRow label="Transactions audit total" value={money(report.transactions_platform_fees_total)} />
              <MetricRow
                label="Stripe processing fees (deposits)"
                value={report.lite ? 'Skipped (lite)' : money(report.stripe_processing_fees)}
              />
              <MetricRow
                label="Subscription revenue"
                value={report.lite ? 'Skipped (lite)' : money(report.subscription_revenue)}
              />
            </Card>

            {warnings.length > 0 && (
              <Card variant="admin" className="p-4 border-amber-500/30">
                <p className={`${typography.microWide} text-amber-400 mb-3`}>Warnings ({warnings.length})</p>
                <ul className="space-y-2">
                  {warnings.map((w) => (
                    <li key={w} className="text-xs font-bold text-amber-200 leading-relaxed">{w}</li>
                  ))}
                </ul>
              </Card>
            )}

            <ExceptionTable
              title="Settlement / claim mismatches"
              rows={report.settlement_claim_payout_mismatches}
              columns={[
                { key: 'challengeId', label: 'Challenge' },
                { key: 'settlementPayout', label: 'Expected', format: money },
                { key: 'actualPayout', label: 'Actual', format: money },
                { key: 'delta', label: 'Delta', format: money },
              ]}
            />
            <ExceptionTable
              title="Stranded withdrawals"
              rows={report.stranded_withdrawals}
              columns={[
                { key: 'id', label: 'Request' },
                { key: 'amount', label: 'Amount', format: money },
                { key: 'status', label: 'Status' },
              ]}
            />
            <ExceptionTable
              title="Stuck withdrawals"
              rows={report.stuck_withdrawals}
              columns={[
                { key: 'id', label: 'Request' },
                { key: 'amount', label: 'Amount', format: money },
                { key: 'status', label: 'Status' },
              ]}
            />
            <ExceptionTable
              title="Negative wallet balances"
              rows={report.negative_wallet_users}
              columns={[
                { key: 'uid', label: 'User' },
                { key: 'walletBalance', label: 'Balance', format: money },
              ]}
            />
          </>
        )}

        <p className="text-[10px] text-gray-600 text-center pb-8">
          Read-only metrics · no wallet, withdrawal, or challenge actions from this screen
        </p>
      </div>
    </div>
  );
}
